import { Button, ChoiceRow, FormBlock, Radio, RadioGroup } from '@westhetics/ui'
import { useId, useRef, type ChangeEvent } from 'react'
import type { LogoPosition } from './api'
import { LOGO_ACCEPT, LOGO_POSITION_LABELS, type LogoChange } from './logo'

/**
 * O bloco Logo de Empresa (docs/areas/administracao.md, "Empresa"; RN-ADM-05): a prévia, o arquivo
 * (escolher, trocar, remover) e a posição no documento. O que a pessoa faz aqui só vale depois de
 * salvar a empresa, e o bloco diz isso. Recebe tudo por props: quem envia o arquivo e guarda a
 * mudança é `useLogoField`. `readOnly` é a tela de quem lê sem editar (RN-ADM-09).
 */

export interface LogoBlockProps {
  /** O que a prévia mostra: o logo salvo ou a imagem que acabou de ser enviada; `null`, sem logo. */
  preview: { url: string; width: number; height: number } | null
  /** O que mudou nesta edição e ainda não foi salvo. */
  change: LogoChange['action']
  position: LogoPosition
  onPositionChange: (position: LogoPosition) => void
  onChoose: (file: File) => void
  onRemove: () => void
  /** Volta ao logo salvo, desfazendo a troca ou a remoção que ainda não foi salva. */
  onUndo: () => void
  /** O arquivo está sendo enviado. */
  uploading?: boolean
  /** Por que o arquivo ou o envio não passou, com causa e caminho. */
  error?: string | null
  readOnly?: boolean
}

/** O estado do logo por extenso: o que está valendo e o que muda ao salvar. */
function statusOf(change: LogoChange['action'], preview: LogoBlockProps['preview']) {
  const size = preview ? `${preview.width}×${preview.height}px` : ''
  if (change === 'replace') {
    return { title: 'Imagem enviada', detail: `${size}. Passa a valer quando você salvar.` }
  }
  if (change === 'remove') {
    return {
      title: 'Logo removido',
      detail: 'Os próximos documentos saem sem logo quando você salvar.',
    }
  }
  if (preview) return { title: 'Logo em uso', detail: size }
  return { title: 'Nenhum logo enviado', detail: 'Os documentos saem sem logo.' }
}

export function LogoBlock({
  preview,
  change,
  position,
  onPositionChange,
  onChoose,
  onRemove,
  onUndo,
  uploading = false,
  error,
  readOnly,
}: LogoBlockProps) {
  const fileInput = useRef<HTMLInputElement>(null)
  const positionLabel = useId()
  const errorId = useId()
  const status = statusOf(change, preview)

  function pick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    // Limpa a escolha: escolher o mesmo arquivo de novo precisa avisar de novo.
    event.target.value = ''
    if (file) onChoose(file)
  }

  const chooseLabel = preview ? 'Trocar' : 'Escolher arquivo'

  return (
    <FormBlock title="Logo" note="PNG ou JPEG, mínimo 200×200px. Aparece em todo documento.">
      <div className="tablet:flex-row tablet:gap-8 flex flex-col gap-5">
        <div className="flex min-w-0 flex-1 items-start gap-5">
          <div
            data-slot="logo-preview"
            className="border-hairline-strong bg-pampas flex size-24 shrink-0 items-center justify-center border"
          >
            {preview ? (
              <img src={preview.url} alt="Logo da clínica" className="size-full object-contain" />
            ) : (
              <span className="font-body text-eyebrow text-label font-light tracking-normal">
                Sem logo
              </span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            <p className="font-body text-lead text-ink leading-[1.3] font-light">{status.title}</p>
            <p className="font-body text-eyebrow text-secondary mt-1 leading-[1.5] font-light tracking-normal">
              {status.detail}
            </p>

            {readOnly ? null : (
              <>
                <input
                  ref={fileInput}
                  type="file"
                  accept={LOGO_ACCEPT}
                  aria-label="Arquivo do logo"
                  tabIndex={-1}
                  className="sr-only"
                  onChange={pick}
                />
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <Button
                    variant={preview ? 'text' : 'outline'}
                    size="sm"
                    loading={uploading}
                    loadingLabel="Enviando…"
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    onClick={() => fileInput.current?.click()}
                  >
                    {chooseLabel}
                  </Button>
                  {uploading ? null : change !== 'keep' ? (
                    <Button variant="text" size="sm" onClick={onUndo}>
                      Desfazer
                    </Button>
                  ) : preview ? (
                    <Button variant="text" size="sm" className="text-plum" onClick={onRemove}>
                      Remover
                    </Button>
                  ) : null}
                </div>
                {error ? (
                  <p
                    id={errorId}
                    role="alert"
                    className="font-body text-eyebrow text-plum mt-2 leading-[1.5] font-light tracking-normal"
                  >
                    {error}
                  </p>
                ) : (
                  <p className="font-body text-eyebrow text-label mt-2 leading-[1.5] font-light tracking-normal">
                    Até 2 MB, com proporção entre 1:2 e 2:1.
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="tablet:w-[260px] tablet:shrink-0">
          <p id={positionLabel} className="eyebrow">
            Posição no documento
          </p>
          <RadioGroup
            aria-labelledby={positionLabel}
            className="mt-[6px]"
            disabled={readOnly}
            value={position}
            onValueChange={(value) => onPositionChange(value as LogoPosition)}
          >
            <ChoiceRow control={<Radio value="header" />} description="No alto da primeira página.">
              {LOGO_POSITION_LABELS.header}
            </ChoiceRow>
            <ChoiceRow
              control={<Radio value="watermark" />}
              description="Ao fundo de todas as páginas do PDF. Não aparece na tela do paciente."
            >
              {LOGO_POSITION_LABELS.watermark}
            </ChoiceRow>
          </RadioGroup>
        </div>
      </div>
    </FormBlock>
  )
}
